// src/components/Auth/authContext.tsx
import React, { createContext, useEffect, useState } from "react";
import * as auth from "./auth";
import { apiFetch } from "../../utils/apiFetch";
import { tenantSlug } from "../../utils/tenantSlug";

interface AuthContextType {
  user: any;
  sub?: string;
  userTenant?: string;
  isAuthenticated: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext({} as AuthContextType);

function extractTenant(u: any): string | undefined {
  if (!u) return undefined;

  const raw =
    u?.attributes?.["custom:company"] ||
    u?.["custom:company"] ||
    u?.signInUserSession?.idToken?.payload?.["custom:company"];

  const val = (raw || "").toString().trim();
  return val ? tenantSlug(val) : undefined;
}

function extractSub(u: any): string | undefined {
  if (!u) return undefined;

  return (
    u?.sub ||
    u?.attributes?.sub ||
    u?.signInUserSession?.idToken?.payload?.sub ||
    u?.username
  );
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const getCurrentUser = async () => {
    try {
      const u = await auth.getCurrentUser();
      return u;
    } catch {
      return null;
    }
  };

  const refreshUser = async () => {
    setIsLoading(true);
    const u = await getCurrentUser();
    setUser(u || null);
    setIsLoading(false);
  };

  const signIn = async (username: string, password: string) => {
    await auth.signIn(username, password);
    await refreshUser();
  };

  const signOut = async () => {
    await auth.signOut();
    setUser(null);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("auth:changed"));
    }
  };

  const sub = extractSub(user);
  const userTenant = extractTenant(user);
  const isAuthenticated = !!user && !!sub;

  useEffect(() => {
    refreshUser();
  }, []);

  useEffect(() => {
    const onAuthChanged = () => {
      refreshUser();
    };
    window.addEventListener("auth:changed", onAuthChanged);
    return () => window.removeEventListener("auth:changed", onAuthChanged);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isAuthenticated) return;

    const inviteCode = (localStorage.getItem("pendingInviteCode") || "")
      .trim()
      .toUpperCase();
    const tenant = (localStorage.getItem("pendingInviteTenant") || "").trim();

    if (!inviteCode) return;

    (async () => {
      const res = await apiFetch("/members/join", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(tenant ? { inviteCode, tenant } : { inviteCode }),
      });

      const data = await res.json().catch(() => ({} as any));

      if (res.ok && data?.ok) {
        localStorage.removeItem("pendingInviteCode");
        localStorage.removeItem("pendingInviteTenant");

        window.dispatchEvent(new Event("auth:changed"));
        return;
      }

      if (res.status === 400 || res.status === 403 || res.status === 409) {
        localStorage.removeItem("pendingInviteCode");
        localStorage.removeItem("pendingInviteTenant");
      }
    })().catch(() => {});
  }, [isAuthenticated]);

  const authValue: AuthContextType = {
    user,
    sub,
    userTenant,
    isAuthenticated,
    isLoading,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
}

export { AuthProvider, AuthContext };
